import { useState, useEffect } from 'react';
import { MessageCircle, X, Send, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inject widget styles
const widgetStyles = `
  .nwth-widget-container * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  .nwth-widget-button {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, #00f0ff 0%, #ff00ff 100%);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.6), 0 0 40px rgba(255, 0, 255, 0.4);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    z-index: 999999;
    animation: nwth-pulse 2s ease-in-out infinite;
  }
  
  .nwth-widget-button:hover {
    transform: scale(1.1);
    box-shadow: 0 0 30px rgba(0, 240, 255, 0.8), 0 0 60px rgba(255, 0, 255, 0.6);
  }
  
  @keyframes nwth-pulse {
    0%, 100% {
      box-shadow: 0 0 20px rgba(0, 240, 255, 0.6), 0 0 40px rgba(255, 0, 255, 0.4);
    }
    50% {
      box-shadow: 0 0 30px rgba(255, 0, 255, 0.8), 0 0 60px rgba(0, 240, 255, 0.6);
    }
  }
  
  .nwth-widget-panel {
    position: fixed;
    bottom: 100px;
    right: 24px;
    width: 400px;
    max-width: calc(100vw - 48px);
    height: 600px;
    max-height: calc(100vh - 150px);
    background: linear-gradient(135deg, #0a0a1f 0%, #1a0a2e 100%);
    border: 2px solid #00f0ff;
    border-radius: 16px;
    box-shadow: 0 0 30px rgba(0, 240, 255, 0.4), 0 0 60px rgba(255, 0, 255, 0.2);
    z-index: 999998;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: nwth-slideIn 0.3s ease-out;
  }
  
  @keyframes nwth-slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .nwth-widget-header {
    background: linear-gradient(135deg, #00f0ff 0%, #b026ff 100%);
    padding: 16px;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .nwth-widget-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    color: #00f0ff;
  }
  
  .nwth-widget-footer {
    padding: 16px;
    border-top: 1px solid #00f0ff;
    background: rgba(10, 10, 31, 0.8);
  }
  
  .nwth-input {
    width: 100%;
    padding: 12px;
    background: rgba(0, 240, 255, 0.1);
    border: 1px solid #00f0ff;
    border-radius: 8px;
    color: #00f0ff;
    font-family: inherit;
    font-size: 14px;
  }
  
  .nwth-input:focus {
    outline: none;
    border-color: #ff00ff;
    box-shadow: 0 0 10px rgba(255, 0, 255, 0.3);
  }
  
  .nwth-textarea {
    width: 100%;
    padding: 12px;
    background: rgba(0, 240, 255, 0.1);
    border: 1px solid #00f0ff;
    border-radius: 8px;
    color: #00f0ff;
    font-family: inherit;
    font-size: 14px;
    min-height: 100px;
    resize: vertical;
  }
  
  .nwth-textarea:focus {
    outline: none;
    border-color: #ff00ff;
    box-shadow: 0 0 10px rgba(255, 0, 255, 0.3);
  }
  
  .nwth-button {
    background: linear-gradient(135deg, #ff00ff 0%, #b026ff 100%);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  
  .nwth-button:hover {
    background: linear-gradient(135deg, #00f0ff 0%, #0080ff 100%);
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.6);
  }
  
  .nwth-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .nwth-service-card {
    background: rgba(0, 240, 255, 0.1);
    border: 1px solid #00f0ff;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .nwth-service-card:hover {
    border-color: #ff00ff;
    background: rgba(255, 0, 255, 0.1);
    box-shadow: 0 0 15px rgba(255, 0, 255, 0.3);
  }
  
  .nwth-service-card.selected {
    border-color: #ff00ff;
    background: rgba(255, 0, 255, 0.2);
  }
  
  .nwth-glow {
    text-shadow: 0 0 10px currentColor;
  }
  
  .nwth-badge {
    display: inline-block;
    padding: 4px 12px;
    background: rgba(0, 255, 0, 0.2);
    border: 1px solid #00ff00;
    border-radius: 12px;
    font-size: 11px;
    color: #00ff00;
    font-weight: bold;
  }
`;

function NextWaveTechWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('welcome'); // welcome, services, form, success
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Inject styles
    const styleId = 'nwth-widget-styles';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = widgetStyles;
      document.head.appendChild(styleElement);
    }

    // Load services
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadServices = async () => {
    try {
      const response = await fetch(`${API}/services`);
      if (response.ok) {
        const data = await response.json();
        setServices(data.services || data);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // For widget, we'll send contact request via email
      const response = await fetch(`${API}/widget/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          service_id: selectedService?.service_id,
          service_name: selectedService?.name
        })
      });

      if (response.ok) {
        setStep('success');
        setTimeout(() => {
          setIsOpen(false);
          setStep('welcome');
          setFormData({ name: '', email: '', title: '', description: '' });
          setSelectedService(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nwth-widget-container">
      {/* Floating Button */}
      <button
        className="nwth-widget-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open chat"
      >
        {isOpen ? (
          <X size={30} color="white" />
        ) : (
          <MessageCircle size={30} color="white" />
        )}
      </button>

      {/* Widget Panel */}
      {isOpen && (
        <div className="nwth-widget-panel">
          {/* Header */}
          <div className="nwth-widget-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={24} />
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                  Next Wave Tech Help
                </h3>
                <p style={{ fontSize: '12px', opacity: 0.9, margin: 0 }}>
                  AI-Powered Tech Support
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
            >
              <X size={24} color="white" />
            </button>
          </div>

          {/* Body */}
          <div className="nwth-widget-body">
            {step === 'welcome' && (
              <div>
                <h2 className="nwth-glow" style={{ fontSize: '24px', marginBottom: '16px' }}>
                  👋 Welcome!
                </h2>
                <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
                  Get expert tech support with our AI-powered assistance and professional team.
                </p>
                <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(255, 0, 255, 0.1)', border: '1px solid #ff00ff', borderRadius: '8px' }}>
                  <p style={{ fontSize: '14px', color: '#ff00ff', fontWeight: 'bold' }}>
                    💳 $75 Diagnostic Fee — Hardware & PC Jobs Only
                  </p>
                  <p style={{ fontSize: '12px', marginTop: '8px', color: '#00f0ff' }}>
                    It's credited toward your total. Remote troubleshooting is a flat $60 with no diagnostic fee!
                  </p>
                </div>
                <button
                  className="nwth-button"
                  onClick={() => setStep('services')}
                  style={{ width: '100%' }}
                >
                  <Zap size={20} />
                  Get Started
                </button>
              </div>
            )}

            {step === 'services' && (
              <div>
                <h2 className="nwth-glow" style={{ fontSize: '20px', marginBottom: '16px' }}>
                  Select a Service
                </h2>
                <div style={{ marginBottom: '16px' }}>
                  {services.map(service => (
                    <div
                      key={service.service_id}
                      className={`nwth-service-card ${selectedService?.service_id === service.service_id ? 'selected' : ''}`}
                      onClick={() => setSelectedService(service)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '24px' }}>{service.icon}</span>
                        <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#ff00ff' }}>
                          {service.pricing_label || `$${service.base_price}${service.price_suffix || ''}`}
                        </span>
                      </div>
                      <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
                        {service.name}
                      </h4>
                      {service.diagnostic_fee_applies ? (
                        <p style={{ fontSize: '12px', opacity: 0.7 }}>$75 diagnostic fee, credited toward total</p>
                      ) : (
                        <span className="nwth-badge">No diagnostic fee</span>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  className="nwth-button"
                  onClick={() => setStep('form')}
                  disabled={!selectedService}
                  style={{ width: '100%' }}
                >
                  Continue
                </button>
              </div>
            )}

            {step === 'form' && (
              <div>
                <h2 className="nwth-glow" style={{ fontSize: '20px', marginBottom: '16px' }}>
                  Contact Information
                </h2>
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      Your Name
                    </label>
                    <input
                      type="text"
                      className="nwth-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="nwth-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      Issue Title
                    </label>
                    <input
                      type="text"
                      className="nwth-input"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Brief description"
                      required
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      Description
                    </label>
                    <textarea
                      className="nwth-textarea"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe your tech issue..."
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="nwth-button"
                    disabled={loading}
                    style={{ width: '100%' }}
                  >
                    {loading ? 'Sending...' : (
                      <>
                        <Send size={20} />
                        Submit Request
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {step === 'success' && (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>✨</div>
                <h2 className="nwth-glow" style={{ fontSize: '24px', marginBottom: '12px', color: '#00ff00' }}>
                  Request Sent!
                </h2>
                <p style={{ lineHeight: '1.6' }}>
                  Thank you for contacting Next Wave Tech Help! We've received your request and will get back to you shortly.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {step !== 'success' && (
            <div className="nwth-widget-footer">
              <p style={{ fontSize: '12px', textAlign: 'center', opacity: 0.7 }}>
                Powered by Next Wave Tech Help 🚀
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NextWaveTechWidget;
